// Lachesis integration bridge.
//
// This is the ONLY meaningful Lachesis-specific surface added to the telegram-tt
// fork (kept in one module so rebasing against upstream stays cheap). It exposes
// `window.__lachesisTg` so the Electron host can:
//   - receive every Telegram API update (set `onUpdate`), and
//   - drive the client (send/read) via the app's own `getActions()` / `getGlobal()`.
// No DOM scraping; this taps telegram-tt's own GramJS update + action layer.

import { getActions, getGlobal } from '../global';

import type { ApiMessage } from '../api/types';
import { ApiMediaFormat } from '../api/types';

import { EDITABLE_INPUT_ID } from '../config';
import { getChatAvatarHash } from '../global/helpers/chats';
import { getMessageMediaHash } from '../global/helpers/messageMedia';
import { getMessageText } from '../global/helpers/messages';
import { selectChatMessage, selectCurrentMessageList, selectPeer } from '../global/selectors';
import { blobToDataUri } from '../util/files';
import { getAllNotificationsCount } from '../util/folderManager';
import { fetch as fetchMedia } from '../util/mediaLoader';

type AnyUpdate = Record<string, unknown>;

export interface LachesisTg {
  // The host assigns this to observe inbound updates (newMessage, ack, etc.).
  onUpdate?: (update: AnyUpdate) => void;
  getActions: typeof getActions;
  getGlobal: typeof getGlobal;
  // Download a message's media as a data URI, so the host can archive it (image,
  // video, voice, audio, document). Returns undefined if the message has no media.
  downloadMedia: (chatId: string, messageId: number) => Promise<{ dataUri: string; mime: string } | undefined>;
  // Download a peer's (chat/group/user) profile photo as a data URI, so the host
  // can show real avatars in the monitor. Returns undefined if the peer has none.
  downloadAvatar: (peerId: string) => Promise<{ dataUri: string; mime: string } | undefined>;
  // Resolve a peer's display name (chat/channel title, else user name / @username /
  // phone) from loaded state, so the host shim never reaches into the global itself.
  getPeerName: (peerId: string) => string;
  // The integer-id messages telegram-tt has loaded into global state (across all
  // chats), for host-side history backfill without touching the global directly.
  getRecentMessages: (limit?: number) => ApiMessage[];
  // The logged-in account's own identity (stable userId + @username / phone / name),
  // so the host can report which Telegram account occupies this port. Undefined
  // until login completes.
  getSelfIdentity: () => { userId: string; username?: string; phone?: string; name: string } | undefined;
  // Total unread count across all (non-archived) chats, for the sidebar badge.
  getUnreadCount: () => number;
  // The currently-open chat (the active message list's peer), or undefined. Powers
  // the host's tools panel (customer profile, AI assistant, quick replies).
  getActiveChat: () => { chatId: string; name: string } | undefined;
  // Open a chat by id via the app's own action (the host's worklist row jump).
  // Returns false on any failure so the host can degrade gracefully.
  openChat: (chatId: string) => boolean;
  // The active chat's recent messages (oldest-first), normalized for the assistant.
  getActiveChatMessages: (limit?: number) => Array<{ fromMe: boolean; text: string }>;
  // Write text into the compose box (replacing its content) so the agent can edit
  // and send — used by quick replies / materials / AI drafts.
  setComposeText: (text: string) => boolean;
  // Send text straight to the currently-open chat (the tools-panel send box).
  sendActiveChatMessage: (text: string) => { ok: boolean; error?: string };
}

// downloadMessageMedia resolves a message's media to bytes via telegram-tt's own
// media loader (no DOM scraping). Photos/stickers use the 'full' size; everything
// else the 'download' size. Defensive: returns undefined on any failure.
async function downloadMessageMedia(
  chatId: string, messageId: number,
): Promise<{ dataUri: string; mime: string } | undefined> {
  try {
    const message = selectChatMessage(getGlobal(), chatId, messageId);
    if (!message) return undefined;
    const content = message.content || {};
    const target = content.photo || content.sticker ? 'full' : 'download';

    const hash = getMessageMediaHash(message, {}, target);
    if (!hash) return undefined;
    const blobUrl = await fetchMedia(hash, ApiMediaFormat.BlobUrl);
    if (!blobUrl || typeof blobUrl !== 'string') return undefined;
    const blob = await (await window.fetch(blobUrl)).blob();
    const dataUri = await blobToDataUri(blob);
    return { dataUri, mime: blob.type };
  } catch {
    return undefined;
  }
}

// downloadPeerAvatar resolves a peer's profile photo to bytes via telegram-tt's
// own media loader (same path the Avatar component uses). Returns undefined if
// the peer has no photo or on any failure.
async function downloadPeerAvatar(
  peerId: string,
): Promise<{ dataUri: string; mime: string } | undefined> {
  try {
    const peer = selectPeer(getGlobal(), peerId);
    if (!peer) return undefined;
    const hash = getChatAvatarHash(peer);
    if (!hash) return undefined;
    // fetchMedia returns the cached blob URL if present, otherwise it kicks off
    // the download and resolves undefined this first time. Retry a few times so a
    // not-yet-cached avatar (e.g. an off-screen/backfilled chat) still resolves.
    let blobUrl = await fetchMedia(hash, ApiMediaFormat.BlobUrl);
    for (let i = 0; i < 6 && (!blobUrl || typeof blobUrl !== 'string'); i++) {
      await new Promise((resolve) => {
        setTimeout(resolve, 700);
      });
      blobUrl = await fetchMedia(hash, ApiMediaFormat.BlobUrl);
    }
    if (!blobUrl || typeof blobUrl !== 'string') return undefined;
    const blob = await (await window.fetch(blobUrl)).blob();
    const dataUri = await blobToDataUri(blob);
    return { dataUri, mime: blob.type };
  } catch {
    return undefined;
  }
}

// resolvePeerName mirrors what the host shim used to compute by reaching into the
// global: chat/channel title, else the user's name / @username / phone.
function resolvePeerName(peerId: string): string {
  try {
    const global = getGlobal();
    const chat = global.chats.byId[peerId];
    if (chat?.title) return chat.title;
    const user = global.users.byId[peerId];
    if (!user) return '';
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (name) return name;
    const username = user.usernames?.[0]?.username;
    if (username) return `@${username}`;
    return user.phoneNumber || '';
  } catch {
    return '';
  }
}

// collectRecentMessages returns the real (integer-id) messages telegram-tt has
// loaded into global state across all chats, capped defensively. Skips optimistic
// local messages (non-integer ids), matching the host's old backfill.
function collectRecentMessages(limit = 3000): ApiMessage[] {
  try {
    const byChatId = getGlobal().messages.byChatId;
    const out: ApiMessage[] = [];
    for (const chat of Object.values(byChatId)) {
      if (!chat?.byId) continue;
      for (const message of Object.values(chat.byId)) {
        if (message && Number.isInteger(message.id)) {
          out.push(message);
          if (out.length >= limit) return out;
        }
      }
    }
    return out;
  } catch {
    return [];
  }
}

// resolveSelfIdentity reads the logged-in account's own user record. Returns
// undefined until login has populated currentUserId + the user object.
function resolveSelfIdentity(): { userId: string; username?: string; phone?: string; name: string } | undefined {
  try {
    const global = getGlobal();
    const userId = global.currentUserId;
    if (!userId) return undefined;
    const user = global.users.byId[userId];
    if (!user) return undefined;
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return { userId, username: user.usernames?.[0]?.username, phone: user.phoneNumber, name };
  } catch {
    return undefined;
  }
}

// countUnread returns the total unread the client itself badges (the All folder's
// notifications count), so the host can mirror it onto the CRM sidebar.
function countUnread(): number {
  try {
    return getAllNotificationsCount();
  } catch {
    return 0;
  }
}

// activeChat returns the chat behind the active message list (the open chat).
function activeChat(): { chatId: string; name: string } | undefined {
  try {
    const list = selectCurrentMessageList(getGlobal());
    if (!list?.chatId) return undefined;
    return { chatId: list.chatId, name: resolvePeerName(list.chatId) };
  } catch {
    return undefined;
  }
}

// openChatById opens a chat through the app's own `openChat` action (same path
// the chat list uses), so the message list, draft, etc. all follow along.
function openChatById(chatId: string): boolean {
  try {
    if (!chatId) return false;
    getActions().openChat({ id: chatId });
    return true;
  } catch {
    return false;
  }
}

// activeChatMessages returns the open chat's recent text messages, oldest-first.
function activeChatMessages(limit = 40): Array<{ fromMe: boolean; text: string }> {
  try {
    const list = selectCurrentMessageList(getGlobal());
    if (!list?.chatId) return [];
    const byId = getGlobal().messages.byChatId[list.chatId]?.byId;
    if (!byId) return [];
    const messages = Object.values(byId).filter((m) => Number.isInteger(m.id));
    messages.sort((a, b) => a.id - b.id);
    return messages
      .slice(-limit)
      .map((m) => ({ fromMe: Boolean(m.isOutgoing), text: getMessageText(m)?.text || '' }))
      .filter((m) => Boolean(m.text.trim()));
  } catch {
    return [];
  }
}

// setComposeText replaces the compose input's content via the browser's own edit
// commands, so telegram-tt's input handler reacts as if the agent had typed it.
function setComposeText(text: string): boolean {
  try {
    const input = document.getElementById(EDITABLE_INPUT_ID);
    if (!input) return false;
    input.focus();
    document.execCommand('selectAll', false);
    document.execCommand('insertText', false, text);
    return true;
  } catch {
    return false;
  }
}

// sendActiveChatMessage sends text to the open chat via the app's own send action.
function sendActiveChatMessage(text: string): { ok: boolean; error?: string } {
  try {
    const list = selectCurrentMessageList(getGlobal());
    if (!list?.chatId) return { ok: false, error: 'no_active_chat' };
    getActions().sendMessage({ messageList: list, text });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error)?.message || 'send_failed' };
  }
}

// The host (Electron preload via contextBridge + injected `window` globals) sets
// these. They are absent in a plain browser, so every consumer must treat them as
// optional and degrade gracefully (the feature simply turns itself off).
interface LachesisHostBridge {
  translate: (body: { text: string; target: string; source?: string }) => Promise<{ text?: string } | undefined>;
}

declare global {
  interface Window {
    __lachesisTg?: LachesisTg;
    __lachesisBridge?: LachesisHostBridge;
    __lachesisTranslateOn?: boolean;
    __lachesisTranslateSendOn?: boolean;
    __lachesisReceiveLang?: string;
    __lachesisSendLang?: string;
  }
}

// lachesisEmitUpdate forwards one Telegram API update to the host, if it hooked in.
// Optional + defensive: the bridge must never break the client.
export function lachesisEmitUpdate(update: AnyUpdate): void {
  try {
    window.__lachesisTg?.onUpdate?.(update);
  } catch {
    // ignore — the host hook is best-effort
  }
}

// installLachesisBridge attaches the host-facing API on `window`, preserving any
// `onUpdate` the host registered before the bundle finished booting.
export function installLachesisBridge(): void {
  const existing = window.__lachesisTg;
  window.__lachesisTg = {
    onUpdate: existing?.onUpdate,
    getActions,
    getGlobal,
    downloadMedia: downloadMessageMedia,
    downloadAvatar: downloadPeerAvatar,
    getPeerName: resolvePeerName,
    getRecentMessages: collectRecentMessages,
    getSelfIdentity: resolveSelfIdentity,
    getUnreadCount: countUnread,
    getActiveChat: activeChat,
    openChat: openChatById,
    getActiveChatMessages: activeChatMessages,
    setComposeText,
    sendActiveChatMessage,
  };
}
